import i18n, { formatBRL } from "@/plugins/i18n";

// Opcao de select com o rotulo no idioma atual. O `text` e um getter: lido no
// render, acompanha a troca de idioma mesmo quando a lista vai para o data().
const translatedOption = (key, value) => ({
  get text() {
    return i18n.t(key);
  },
  value,
});

export const WEEK_DAY_ITEMS = [0, 1, 2, 3, 4, 5, 6].map((day) =>
  translatedOption(`enums.weekDay.${day}`, day)
);

export const ENGLISH_LEVELS = [
  { text: "Beginner 1", value: 0 },
  { text: "Beginner 2", value: 1 },
  { text: "Essential 1", value: 2 },
  { text: "Essential 2", value: 3 },
  { text: "Pre Intermediate 1", value: 4 },
  { text: "Pre Intermediate 2", value: 5 },
  { text: "Intermediate 1", value: 6 },
  { text: "Intermediate 2", value: 7 },
  { text: "Upper 1", value: 8 },
  { text: "Upper 2", value: 9 },
  { text: "Advanced 1", value: 10 },
  { text: "Advanced 2", value: 11 },
];

// Trilha de conversação: cursos publicados nos níveis 99/100/101, fora da
// escada regular. Uma aula de conversação pode substituir a aula prevista da
// turma, então o professor precisa vê-la ao trocar o assunto de uma aula.
export const CONVERSATION_LEVELS = [99, 100, 101].map((level) =>
  translatedOption(`enums.conversationLevel.${level}`, level)
);

export const ENGLISH_LEVELS_WITH_CONVERSATION = [
  ...ENGLISH_LEVELS,
  ...CONVERSATION_LEVELS,
];

// Card 17trt6je3uq: trilhas Instrumental (102) e Outros (103). Como a
// conversação, ficam fora da escada e o aluno só recebe a aula quando o
// professor a escolhe como assunto -- por isso entram só na troca de assunto.
export const TRACK_LEVELS = [102, 103].map((level) =>
  translatedOption(`enums.trackLevel.${level}`, level)
);

export const SUBJECT_LEVELS = [
  ...ENGLISH_LEVELS_WITH_CONVERSATION,
  ...TRACK_LEVELS,
];

// Modalidades que uma turma gerenciada aceita. 0 (Sozinho) e 5 (Quinteto) ficam
// de fora porque `resolveManagedGroupCapacity`, na API, so aceita de 1 a 4.
// O valor e, ele proprio, a capacidade em alunos.
export const GROUP_FORMAT_OPTIONS = [1, 2, 3, 4].map((format) =>
  translatedOption(`enums.groupFormat.${format}`, format)
);

// Rotulo de um enum pela chave de traducao; "-" quando o valor nao existe.
const enumLabel = (group, value, keys, fallback = "-") =>
  keys.includes(+value) ? i18n.t(`enums.${group}.${+value}`) : fallback;

// Enum PerWeek da API. "A cada 15 dias" nao sai da grade (a turma quinzenal tem
// um unico dia da semana), por isso a frequencia e um campo proprio e nao um
// derivado de weekDays.
export const GROUP_FREQUENCY_OPTIONS = [1, 2, 3, 4, 0].map((frequency) =>
  translatedOption(`enums.groupFrequency.${frequency}`, frequency)
);

// Frequencia que a grade sugere, contando dias da semana distintos -- espelha
// resolveFrequencyByScheduleCount da API. A quinzenal fica de fora de proposito:
// nao ha como deduzi-la do numero de dias.
export function frequencyFromSchedule(weekDays = [], hours = []) {
  const uniqueDays = new Set(
    normalizeClassTimes(weekDays, hours).map((time) => +time.weekDay)
  );

  if (!uniqueDays.size) return null;
  if (uniqueDays.size === 1) return 1;
  if (uniqueDays.size === 2) return 2;
  return 3;
}

const GROUP_STATUS_COLORS = {
  0: "amber darken-2",
  1: "green darken-1",
  2: "blue-grey lighten-1",
  3: "red darken-1",
};

export function displayGroupLevel(level) {
  return ENGLISH_LEVELS.find((item) => item.value === +level)?.text || "-";
}

export function displayGroupFormat(format) {
  return enumLabel("groupFormat", format, [0, 1, 2, 3, 4, 5]);
}

export function displayGroupFrequency(frequency) {
  return enumLabel("groupFrequency", frequency, [0, 1, 2, 3, 4]);
}

// Plano de ensino do professor usa OUTRO enum (PerWeekPlans da API): 4 = 4x
// por semana e 5 = quinzenal. Passar a frequencia do plano por
// displayGroupFrequency mostrava o plano de 4x como "A cada 15 dias" e o
// quinzenal como "-".
export function displayPlanFrequency(frequency) {
  return enumLabel("planFrequency", frequency, [1, 2, 3, 4, 5]);
}

export function displayGroupStatus(status) {
  return enumLabel("groupStatus", status, [0, 1, 2, 3]);
}

export function groupStatusColor(status) {
  return GROUP_STATUS_COLORS[+status] || "grey";
}

export function participantStatusLabel(status) {
  return enumLabel(
    "participantStatus",
    status,
    [0, 1, 2, 3],
    i18n.t("enums.participantStatus.none")
  );
}

export function participantStatusColor(status) {
  return groupStatusColor(status);
}

export function weekDayShortLabel(weekDay) {
  return enumLabel("weekDayShort", weekDay, [0, 1, 2, 3, 4, 5, 6]);
}

export function formatScheduleSummary(weekDays = [], hours = []) {
  const times = normalizeClassTimes(weekDays, hours);

  if (!times.length) {
    return i18n.t("enums.noSchedule");
  }

  return times
    .map((time) => `${weekDayShortLabel(time.weekDay)} ${time.start}`)
    .join(" · ");
}

export function attendanceStatusLabel(status) {
  if (+status === 1) {
    return i18n.t("enums.attendance.present");
  }

  if (+status === 2) {
    return i18n.t("enums.attendance.absent");
  }

  return i18n.t("enums.attendance.pending");
}

export function normalizeClassTimes(weekDays = [], hours = []) {
  if (!Array.isArray(weekDays) || !Array.isArray(hours)) return [];

  return weekDays.reduce((acc, day, index) => {
    const hour = hours[index];

    if (!hour || !hour.includes("-")) return acc;

    const [start, end] = hour.split("-");
    acc.push({
      weekDay: +day,
      start: start.trim(),
      end: end.trim(),
    });

    return acc;
  }, []);
}

export function initialsFromName(name) {
  if (!name) return "";

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function resolveBillingValue(student) {
  const candidate =
    student?.billing?.monthlyValue ??
    student?.currentPlan?.finalValue ??
    student?.currentPlan?.value ??
    student?.monthlyFee ??
    student?.mensalidade ??
    student?.plan?.value;

  if (candidate === undefined || candidate === null || candidate === "") {
    return null;
  }

  const numericCandidate =
    typeof candidate === "number" ? candidate : Number(candidate);

  if (!Number.isNaN(numericCandidate)) {
    return formatBRL(numericCandidate);
  }

  return `${candidate}`;
}

export function resolveTeachingPlanValue(plan) {
  if (!plan) {
    return null;
  }

  const value = Number(plan.value || 0);
  const discount = Number(plan.discount || 0);

  if (Number.isNaN(value) || Number.isNaN(discount)) {
    return null;
  }

  return Math.max(value - discount, 0);
}

export function getClassDurationValue(hours = []) {
  const firstRange = Array.isArray(hours) ? hours[0] : null;

  if (!firstRange || !firstRange.includes("-")) return null;

  const [start, end] = firstRange.split("-").map((item) => item.trim());
  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  const startTotal = startHour * 60 + startMinute;
  const endTotal = endHour * 60 + endMinute;
  const duration = endTotal - startTotal;

  if (Number.isNaN(duration) || duration <= 0) return null;

  return duration > 60 ? 2 : 1;
}

export function findMatchingTeachingPlan(plans = [], classData = {}) {
  if (!Array.isArray(plans) || !plans.length || !classData) return null;

  const classFormat = Number(classData.format);
  const classFrequency = Number(classData.frequency);

  return (
    plans.find((plan) => {
      const sameFormat = Number(plan.format) === classFormat;
      const sameFrequency = Number(plan.frequency) === classFrequency;

      return sameFormat && sameFrequency;
    }) ||
    plans.find(
      (plan) =>
        Number(plan.format) === classFormat &&
        Number(plan.frequency) === classFrequency
    ) ||
    null
  );
}

// Nome da turma do jeito que o professor a reconhece: pelos alunos dela, e nao
// pelo codigo (cards 86ak5yynk e 86akng735). Usado no compartilhamento de
// anotacoes e na inclusao de aula complementar -- os dois seguem iguais porque
// saem daqui.
export function groupLabelByStudents(group) {
  const names = ((group && group.content) || [])
    .map((student) => String(student.name || "").trim())
    .filter(Boolean);

  return names.length ? names.join(", ") : i18n.t("enums.groupWithoutStudents");
}
